import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Paper,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GridSortModel,
  gridExpandedSortedRowIdsSelector,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import StyledDataGrid from 'shared/components/StyledDataGrid';
import CreativePerformanceService, { MAX_ROWS } from 'services/CreativePerformanceService';
import FilterBar from './components/FilterBar';
import { buildColumns } from './columns';
import useUrlFilters from './useUrlFilters';
import { buildAssetColumns } from './assetColumns';
import CreativePreview, { PreviewCreative } from './components/CreativePreview';
import VariantsDialog, { VariantsRequest } from './components/VariantsDialog';
import ColumnSettings from './components/ColumnSettings';
import FilterBuilder from './components/FilterBuilder';
import {
  FilterRule,
  describeRule,
  fieldsFromColumns,
  isValidRule,
  matchesRules,
} from './filterRules';
import useColumnLayout from './useColumnLayout';
import {
  AssetPerformanceResponse,
  AssetRow,
  CoverageResponse,
  CreativePerformanceResponse,
  CreativeQuery,
  CreativeRow,
  PremiumStatus,
  VariantRow,
} from './types';
import {
  MEDIA_LABELS,
  adsManagerUrl,
  assetUrl,
  creativeImage,
  fmtInt,
  fmtMoney,
  fmtMoneyExact,
  fmtRatio,
  isRollup,
  summarizeAds,
  summarizeAssets,
  DEFAULT_HIDDEN_COLUMNS,
  ExportScope,
  PAGE_SIZE_OPTIONS,
  exportFileName,
  postUrl,
  toNum,
  videoUrl,
} from './utils';

// `help` spells out the arithmetic behind a derived total, the same way the
// adjusted columns explain themselves on hover.
function StatBlock({
  label,
  value,
  help,
}: {
  label: string;
  value: string;
  help?: string | null;
}) {
  const block = (
    <Box sx={help ? { cursor: 'help' } : undefined}>
      <Typography
        variant="caption"
        sx={{
          color: '#64748B',
          display: 'block',
          ...(help ? { borderBottom: '1px dotted #94A3B8' } : {}),
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
  return help ? (
    <Tooltip title={help} placement="top">
      {block}
    </Tooltip>
  ) : (
    block
  );
}

export default function CreativePerformancePage({
  active = true,
}: {
  active?: boolean;
}) {
  const [filters, updateFilters, resetFilters] = useUrlFilters(active);
  const [rows, setRows] = useState<CreativeRow[]>([]);
  const [assetRows, setAssetRows] = useState<AssetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewCreative | null>(null);
  const [variantsRequest, setVariantsRequest] = useState<VariantsRequest | null>(null);
  const [variantRows, setVariantRows] = useState<VariantRow[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const [coverage, setCoverage] = useState<CoverageResponse | null>(null);
  // The incrementality multiplier and whether premium could be sourced both come
  // from the response, so the page never keeps its own copy of either.
  const [multiplier, setMultiplier] = useState<number | null>(null);
  const [premiumStatus, setPremiumStatus] = useState<PremiumStatus | null>(null);

  // The grid owns sorting and pagination, so it is the only thing that knows
  // which rows are on screen and in what order. The export asks it.
  const apiRef = useGridApiRef();

  const service = useMemo(() => new CreativePerformanceService(), []);
  // Filters change fast; without this a slow early request can overwrite the
  // results of a later one.
  const fetchIdRef = useRef(0);

  // Only the parameters the request actually carries. Depending on the whole
  // filters object would re-fetch — and blank the table behind a spinner — every
  // time the user toggled a media chip, added a rule or changed the page size,
  // none of which change what the API returns.
  const { start, end, groupBy, level, assetType } = filters;

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    const isAssetLevel = level === 'asset';
    const query: CreativeQuery = { start, end, groupBy, level, assetType };

    const result = isAssetLevel
      ? await service.getAssets(query)
      : await service.getAds(query);

    if (fetchId !== fetchIdRef.current) return;

    if (!result) {
      setError('Could not load creative performance. The API may be unavailable.');
      setRows([]);
      setAssetRows([]);
    } else if (isAssetLevel) {
      setAssetRows(((result as { rows: AssetRow[] }).rows || []) as AssetRow[]);
      setRows([]);
      setMultiplier((result as AssetPerformanceResponse).meta?.trueRoasMultiplier ?? null);
      // Asset level has no premium — reach is an ad-level fact — so there is no
      // status to carry, and last level's must not linger.
      setPremiumStatus(null);
    } else {
      setRows(((result as { rows: CreativeRow[] }).rows || []) as CreativeRow[]);
      setAssetRows([]);
      const meta = (result as CreativePerformanceResponse).meta;
      setMultiplier(meta?.trueRoasMultiplier ?? null);
      setPremiumStatus(meta?.premiumStatus ?? null);
    }
    setLoading(false);
  }, [service, start, end, groupBy, level, assetType]);

  useEffect(() => {
    load();
  }, [load]);

  const isAssetLevel = filters.level === 'asset';

  const previewAd = useCallback((row: CreativeRow) => {
    const links = [
      { label: 'Ads Manager', href: adsManagerUrl(row.account_id, row.ad_id) },
      { label: 'View post', href: postUrl(row.post_id) },
      { label: 'Video', href: videoUrl(row.video_id) },
      { label: 'Landing page', href: row.destination_url },
    ].filter((link): link is { label: string; href: string } => !!link.href);

    // The proxy always returns a panel-sized rendition, so the "this is only a
    // 64px thumbnail" caveat applies solely to the raw-Meta fallback path.
    const image = creativeImage(row.thumbnail_url, row.image_url);

    setPreview({
      title: row.ad_name || row.group_key,
      mediaLabel: MEDIA_LABELS[row.media_type],
      imageUrl: row.image_proxy_url || image.url,
      imageIsThumbnail: !row.image_proxy_url && image.isThumbnail,
      headline: row.headline,
      primaryText: row.primary_text,
      ctaType: row.cta_type,
      links,
      meta: [
        { label: 'Spend', value: fmtMoney(row.spend) },
        { label: 'Revenue', value: fmtMoney(row.revenue) },
        { label: 'Profit', value: fmtMoney(row.profit) },
        { label: 'ROAS', value: fmtRatio(row.roas) },
        { label: 'Campaign', value: row.campaign_name || '–' },
      ],
    });
  }, []);

  const previewAsset = useCallback(
    (row: AssetRow) => {
      const href = assetUrl(row.asset_url);
      setPreview({
        title: row.asset_text || row.asset_name || 'Asset',
        mediaLabel: filters.assetType.replace('_', ' '),
        // Image assets are a directly viewable URL; video assets are a reel
        // permalink, so a still of the ad that ran it is all we can render — and
        // that comes from the proxy, because asset_metrics_daily's own
        // thumbnail_url is written once a day and never refreshed.
        imageUrl:
          filters.assetType === 'image'
            ? href
            : row.image_proxy_url || creativeImage(row.thumbnail_url).url,
        headline: filters.assetType === 'headline' ? row.asset_text : null,
        primaryText: filters.assetType === 'primary_text' ? row.asset_text : null,
        ctaType: null,
        links: href ? [{ label: 'View on Facebook', href }] : [],
        meta: [
          { label: 'Spend', value: fmtMoney(row.spend) },
          { label: 'Meta ROAS', value: fmtRatio(row.meta_roas) },
          { label: 'Purchases', value: fmtInt(row.purchases) },
          { label: 'Ads using it', value: fmtInt(row.ad_count) },
        ],
      });
    },
    [filters.assetType],
  );

  const openVariants = useCallback(
    async (row: CreativeRow, assetType: 'headline' | 'primary_text') => {
      const stored =
        (assetType === 'headline' ? row.headline_variants : row.primary_text_variants) ||
        [];
      setVariantsRequest({
        adId: row.ad_id,
        adName: row.ad_name || row.group_key,
        assetType,
        storedVariants: stored,
      });
      setVariantRows([]);

      if (!row.ad_id) return;
      setVariantsLoading(true);
      const result = await service.getVariants(
        row.ad_id,
        assetType,
        filters.start,
        filters.end,
      );
      setVariantRows(result?.rows || []);
      setVariantsLoading(false);
    },
    [service, filters.start, filters.end],
  );

  const columns = useMemo(
    () =>
      isAssetLevel
        ? buildAssetColumns(filters.assetType, previewAsset, multiplier)
        : buildColumns(
            filters.groupBy,
            filters.mediaType,
            previewAd,
            openVariants,
            multiplier,
          ),
    [
      isAssetLevel,
      filters.assetType,
      filters.groupBy,
      filters.mediaType,
      previewAd,
      previewAsset,
      openVariants,
      multiplier,
    ],
  );

  // Every column the table can show is filterable, including hidden ones — the
  // rule set is about the data, not the current layout.
  const filterFields = useMemo(() => fieldsFromColumns(columns), [columns]);

  // A link can carry rules for a level that doesn't have those columns. Those
  // are dropped silently rather than reported.
  const activeRules = useMemo(
    () => filters.rules.filter(rule => isValidRule(rule, filterFields)),
    [filters.rules, filterFields],
  );

  const ruleLabels = useMemo(
    () => activeRules.map(rule => describeRule(rule, filterFields)),
    [activeRules, filterFields],
  );

  const addRule = useCallback(
    (rule: FilterRule) => updateFilters({ rules: [...activeRules, rule] }),
    [updateFilters, activeRules],
  );

  const removeRule = useCallback(
    (index: number) =>
      updateFilters({ rules: activeRules.filter((_, i) => i !== index) }),
    [updateFilters, activeRules],
  );

  const clearRules = useCallback(() => updateFilters({ rules: [] }), [updateFilters]);

  const allRows: (CreativeRow | AssetRow)[] = isAssetLevel ? assetRows : rows;

  // Zero-spend rows are almost always ads that were created but never really
  // delivered — noise in a performance table.
  // The API returns the whole window; media type, search, filter rules and
  // zero-spend are applied here so adjusting any of them is instant rather than
  // a round trip.
  //
  // Applied in two stages so the caption below can name the two reasons rows
  // are missing separately. Reporting a single difference would have described
  // every rule-excluded row as zero-spend.
  const { gridRows, zeroSpendHidden, filterHidden } = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();

    const spent = filters.hideZeroSpend
      ? allRows.filter(row => (toNum(row.spend) || 0) > 0)
      : allRows;

    const kept = spent.filter(row => {
      if (!matchesRules(row as unknown as Record<string, unknown>, activeRules, filterFields)) {
        return false;
      }

      if (!isAssetLevel) {
        const adRow = row as CreativeRow;
        if (filters.mediaType !== 'all' && adRow.media_type !== filters.mediaType) {
          return false;
        }
        if (needle) {
          const haystack = [adRow.ad_name, adRow.headline, adRow.primary_text]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
      } else if (needle) {
        const assetRow = row as AssetRow;
        const haystack = [assetRow.asset_text, assetRow.asset_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      return true;
    });

    return {
      gridRows: kept as (CreativeRow | AssetRow)[],
      zeroSpendHidden: allRows.length - spent.length,
      filterHidden: spent.length - kept.length,
    };
  }, [
    allRows,
    isAssetLevel,
    filters.hideZeroSpend,
    filters.mediaType,
    filters.search,
    activeRules,
    filterFields,
  ]);

  // The API caps each response, so a wide window can be cut before the browser
  // ever sees it — and the rows that fall off are the lowest-spending ones,
  // since that is the order the API returns. Said out loud rather than left to
  // look like a complete set.
  const truncated = allRows.length >= MAX_ROWS;

  // Coverage describes the data behind the video columns, which only the
  // ad-level table shows.
  useEffect(() => {
    if (level === 'asset') {
      setCoverage(null);
      return undefined;
    }
    let cancelled = false;
    service.getCoverage(start, end).then(result => {
      if (!cancelled) setCoverage(result ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [service, start, end, level]);

  // Without this an empty video cell reads as "nobody watched". It means Meta
  // reported no measurable video delivery for that ad, which is a different
  // claim — and for anything before the backfill, no claim at all.
  const coverageNote = useMemo(() => {
    if (!coverage || !coverage.videoAds) return null;
    const parts = [
      `Video engagement collected for ${fmtInt(coverage.videoAdsWithMetrics)} of ` +
        `${fmtInt(coverage.videoAds)} video ads — a blank video column means Meta ` +
        `reported no measurable delivery, not zero.`,
    ];
    const latest = coverage.videoMetricsLatestDate?.slice(0, 10);
    if (latest && latest < end) {
      parts.push(`Collected through ${latest}.`);
    }
    return parts.join(' ');
  }, [coverage, end]);

  const totals = useMemo(
    () => (isAssetLevel ? null : summarizeAds(gridRows as CreativeRow[])),
    [isAssetLevel, gridRows],
  );
  const assetTotals = useMemo(
    () => (isAssetLevel ? summarizeAssets(gridRows as AssetRow[]) : null),
    [isAssetLevel, gridRows],
  );

  const {
    visibleColumns,
    orderedFields,
    hiddenFields,
    pinnedFields,
    pinnedColumns,
    toggleField,
    togglePin,
    reorderFields,
    reset: resetColumns,
    hiddenCount,
  } = useColumnLayout(filters.level, columns, DEFAULT_HIDDEN_COLUMNS[filters.level]);

  const [page, setPage] = useState(0);
  const paginationModel = useMemo(
    () => ({ page, pageSize: filters.pageSize }),
    [page, filters.pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPage(model.page);
      if (model.pageSize !== filters.pageSize) {
        updateFilters({ pageSize: model.pageSize });
        setPage(0);
      }
    },
    [filters.pageSize, updateFilters],
  );

  // A filter change can shrink the result set below the current page, which
  // would otherwise leave the grid showing an empty page.
  useEffect(() => {
    setPage(0);
  }, [
    filters.start,
    filters.end,
    filters.mediaType,
    filters.groupBy,
    filters.level,
    filters.assetType,
    filters.search,
    filters.hideZeroSpend,
    filters.rules,
  ]);

  const sortModel = useMemo<GridSortModel>(
    () => [{ field: filters.sort, sort: filters.direction }],
    [filters.sort, filters.direction],
  );

  // Sorting runs in the warehouse, not the browser: the grid only holds the top
  // N rows, so sorting client-side would reorder a slice rather than the set.
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const next = model[0];
      if (!next) return;
      updateFilters({
        sort: next.field,
        direction: next.sort === 'asc' ? 'asc' : 'desc',
      });
    },
    [updateFilters],
  );

  // Straight to the grid's own CSV writer rather than a hand-rolled one: it
  // already quotes commas, quotes and newlines — which headlines and primary
  // text are full of — and it serialises each column's valueGetter output, so
  // the file carries raw numbers (1234.56) instead of the "$1.2k" the cells
  // render. Columns follow the Columns dialog, because the grid is only given
  // the visible ones.
  const handleExport = useCallback(
    (scope: ExportScope) => {
      if (!apiRef.current) return;
      // Both selectors return the grid's current sort order. gridRows would not:
      // it is filtered but unsorted and unpaginated, so exporting it would hand
      // back rows in a different order than the table shows.
      const rowIds =
        scope === 'page'
          ? gridPaginatedVisibleSortedGridRowIdsSelector(apiRef)
          : gridExpandedSortedRowIdsSelector(apiRef);

      apiRef.current.exportDataAsCsv({
        getRowsToExport: () => rowIds,
        fileName: exportFileName(filters, scope, rowIds.length, truncated),
        // Excel reads a plain UTF-8 CSV as Latin-1 and mangles the curly
        // apostrophes Meta copy is written with.
        utf8WithBom: true,
      });
    },
    [apiRef, filters, truncated],
  );

  // Label only — the export itself counts the ids it actually got.
  const pageRowCount = Math.max(
    0,
    Math.min(paginationModel.pageSize, gridRows.length - paginationModel.page * paginationModel.pageSize),
  );


  return (
    // Full width: this table has a lot of columns and the horizontal room is
    // worth more than a centred measure.
    <Box sx={{ px: 2, py: 2, width: '100%' }}>
      {/* The page title lives in CreativePerformanceTabs, above the tabs. */}
      <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
        Every Dosaze US Meta ad, what it looked like, and what it returned. Triple
        Whale numbers are true revenue and margin; Meta numbers are what the
        platform claims.
      </Typography>

      <FilterBar
        filters={filters}
        onChange={updateFilters}
        onReset={resetFilters}
        onOpenColumns={() => setColumnsOpen(true)}
        onAddFilter={() => setFilterBuilderOpen(true)}
        onRemoveRule={removeRule}
        onClearRules={clearRules}
        ruleLabels={ruleLabels}
        hiddenColumnCount={hiddenCount}
        onExport={handleExport}
        pageRowCount={pageRowCount}
        totalRowCount={gridRows.length}
        disabled={loading}
      />

      {!isAssetLevel &&
        (filters.groupBy === 'headline' || filters.groupBy === 'primary_text') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This groups <strong>ads</strong> by the copy that led them, and credits
            each ad's whole spend and margin to that one variant — even though
            Advantage+ rotated others alongside it. Useful because it is the only
            view with Triple Whale revenue and profit. For what a headline itself earned,
            switch to <strong>Asset level</strong>, where Meta attributes spend to
            the individual variant.
          </Alert>
        )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing the {fmtInt(MAX_ROWS)} highest-spending{' '}
          {isAssetLevel ? 'assets' : 'rows'} for this range — there are more, and
          the totals below describe only what's shown. Narrow the date range to
          see a complete set.
        </Alert>
      )}

      {(zeroSpendHidden > 0 || filterHidden > 0) && (
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
          {[
            zeroSpendHidden > 0 &&
              `${fmtInt(zeroSpendHidden)} zero-spend row${
                zeroSpendHidden === 1 ? '' : 's'
              } hidden`,
            filterHidden > 0 &&
              `${fmtInt(filterHidden)} row${
                filterHidden === 1 ? '' : 's'
              } excluded by filters`,
          ]
            .filter(Boolean)
            .join(' · ')}
          .
        </Typography>
      )}

      {coverageNote && (
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
          {coverageNote}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Premium comes from Meta at request time and can fail on its own. Saying
          so matters: a blank Premium column has to read as "couldn't ask", never
          as "this creative isn't fatigued". */}
      {premiumStatus && !premiumStatus.ok && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {premiumStatus.message}
        </Alert>
      )}


      {isAssetLevel && assetTotals && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatBlock label="Assets / ads" value={`${fmtInt(assetTotals.assets)} / ${fmtInt(assetTotals.ads)}`} />
            <Divider orientation="vertical" flexItem />
            <StatBlock label="Spend" value={fmtMoneyExact(assetTotals.spend)} />
            <StatBlock label="Meta purch. value" value={fmtMoneyExact(assetTotals.purchaseValue)} />
            <StatBlock label="Meta ROAS" value={fmtRatio(assetTotals.metaRoas)} />
            <StatBlock
              label="True ROAS"
              value={fmtRatio(assetTotals.trueRoas)}
              help={
                multiplier === null
                  ? null
                  : `Meta ROAS ${fmtRatio(
                      assetTotals.metaRoas,
                    )} × ${multiplier} = ${fmtRatio(assetTotals.trueRoas)}`
              }
            />
            <StatBlock label="Purchases" value={fmtInt(assetTotals.purchases)} />
            <StatBlock label="Meta CPA" value={fmtMoneyExact(assetTotals.metaCpa)} />
          </Box>
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 1 }}>
            Asset level is Meta-reported only. Triple Whale attributes revenue to an ad, not to
            the asset Meta chose to show for a given impression, so there is no Triple Whale
            revenue or profit at this grain — switch to Ad level for margin. True ROAS works here
            because it only ever scales Meta's own figures.
          </Typography>
        </Paper>
      )}

      {!isAssetLevel && totals && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <StatBlock
              label={isRollup(filters.groupBy) ? 'Groups / ads' : 'Ads'}
              value={
                isRollup(filters.groupBy)
                  ? `${fmtInt(totals.rows)} / ${fmtInt(totals.ads)}`
                  : fmtInt(totals.ads)
              }
            />
            <Divider orientation="vertical" flexItem />
            <StatBlock label="Spend" value={fmtMoneyExact(totals.spend)} />
            <StatBlock label="Revenue" value={fmtMoneyExact(totals.revenue)} />
            <StatBlock label="Profit" value={fmtMoneyExact(totals.profit)} />
            <StatBlock label="ROAS" value={fmtRatio(totals.roas)} />
            <StatBlock label="Meta ROAS" value={fmtRatio(totals.metaRoas)} />
            <StatBlock
              label="True revenue"
              value={fmtMoneyExact(totals.trueRevenue)}
              help={
                multiplier === null
                  ? null
                  : `Meta purchase value ${fmtMoneyExact(
                      totals.metaPurchaseValue,
                    )} × ${multiplier} = ${fmtMoneyExact(totals.trueRevenue)}`
              }
            />
            <StatBlock
              label="True profit"
              value={fmtMoneyExact(totals.trueProfit)}
              help="Each row's true revenue charged at its own Triple Whale margin, minus its spend, then summed. Only rows that have both a Meta purchase value and Triple Whale revenue can be computed, so this covers fewer rows than Profit — the two totals are not comparable."
            />
            <StatBlock
              label="True ROAS"
              value={fmtRatio(totals.trueRoas)}
              help={
                multiplier === null
                  ? null
                  : `Meta ROAS ${fmtRatio(totals.metaRoas)} × ${multiplier} = ${fmtRatio(
                      totals.trueRoas,
                    )}`
              }
            />
            <StatBlock label="Orders" value={fmtInt(totals.orders)} />
            <StatBlock label="CPA" value={fmtMoneyExact(totals.cpa)} />
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : gridRows.length === 0 && !error ? (
        <Alert severity="info">
          {isAssetLevel
            ? 'No assets matched these filters in this date range.'
            : 'No ads matched these filters in this date range.'}
        </Alert>
      ) : (
        <>
          {/* Mirrors the grid's own footer control so a long table can be paged
              without scrolling to the bottom first. Both read and write the same
              paginationModel, so they can't drift apart. */}
          <TablePagination
            component="div"
            count={gridRows.length}
            page={paginationModel.page}
            rowsPerPage={paginationModel.pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, nextPage) =>
              handlePaginationModelChange({
                page: nextPage,
                pageSize: paginationModel.pageSize,
              })
            }
            onRowsPerPageChange={event =>
              handlePaginationModelChange({
                page: 0,
                pageSize: Number(event.target.value),
              })
            }
            sx={{
              borderBottom: 'none',
              '& .MuiTablePagination-toolbar': { minHeight: 40, pl: 0 },
            }}
          />
          <StyledDataGrid
          apiRef={apiRef}
          autoHeight
          rows={gridRows}
          columns={visibleColumns}
          getRowId={row => row.group_key}
          getRowHeight={() => 'auto'}
          columnHeaderHeight={52}
          disableRowSelectionOnClick
          // The per-column ⋮ menu only offers sort and hide-column, both of
          // which the header click and the Columns dialog already do — and on
          // these narrow numeric columns it appeared on hover and clipped the
          // header text mid-word.
          disableColumnMenu
          pagination
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pinnedColumns={pinnedColumns}
          sx={{
            '& .MuiDataGrid-cell': { py: '8px', alignItems: 'flex-start' },
            // Let a header wrap onto a second line rather than truncate once the
            // sort arrow claims part of the width.
            '& .MuiDataGrid-columnHeader': { alignItems: 'flex-start', pt: '4px' },
            '& .MuiDataGrid-columnHeaderTitleContainer': { overflow: 'visible' },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 500,
              whiteSpace: 'normal',
              lineHeight: 1.2,
              overflow: 'visible',
              textOverflow: 'clip',
            },
            // The sort arrow is only rendered on hover, which reflowed the
            // header every time the pointer crossed it. Reserve its space
            // permanently and keep it invisible unless the column is sorted, so
            // hovering changes nothing about the layout.
            '& .MuiDataGrid-iconButtonContainer': {
              width: 20,
              visibility: 'visible',
            },
            '& .MuiDataGrid-sortIcon': { opacity: 0 },
            '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-sortIcon': {
              opacity: 1,
            },
          }}
          />
        </>
      )}

      <CreativePreview creative={preview} onClose={() => setPreview(null)} />

      <VariantsDialog
        request={variantsRequest}
        rows={variantRows}
        loading={variantsLoading}
        onClose={() => setVariantsRequest(null)}
      />

      <FilterBuilder
        open={filterBuilderOpen}
        fields={filterFields}
        onAdd={addRule}
        onClose={() => setFilterBuilderOpen(false)}
      />

      <ColumnSettings
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        columns={columns}
        orderedFields={orderedFields}
        hiddenFields={hiddenFields}
        pinnedFields={pinnedFields}
        onToggle={toggleField}
        onTogglePin={togglePin}
        onReorder={reorderFields}
        onReset={resetColumns}
      />
    </Box>
  );
}
